import axios from 'axios';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { AuthLayout } from '../auth/AuthLayout';

const schema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm: z.string().min(8, 'Confirm your new password'),
  })
  .refine((v) => v.password === v.confirm, {
    path: ['confirm'],
    message: 'Passwords must match',
  });

type Values = z.infer<typeof schema>;

export function ResetPasswordScreen() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirm: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    if (!token) {
      setError('Reset link is missing or invalid.');
      return;
    }
    try {
      await axios.post(
        '/api/auth/reset-password',
        { token, password: values.password },
        { withCredentials: false, headers: { Accept: 'application/json' } },
      );
      setSuccess(true);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 400 || status === 404 || status === 410)
        setError('This reset link has expired. Request a new one from the sign-in screen.');
      else setError('Something went wrong. Please try again.');
    }
  });

  if (success) {
    return (
      <AuthLayout title="Password updated" subtitle="You're all set. Sign back in to continue.">
        <div className="flex flex-col items-center gap-4 py-2">
          <CheckCircle2 className="size-10 text-success" />
          <Button asChild size="lg" className="w-full">
            <Link to="/sign-in">Sign in</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Pick something strong — at least 8 characters."
      footer={
        <Link
          to="/sign-in"
          className="font-medium text-foreground underline-offset-4 hover:underline"
        >
          Back to sign in
        </Link>
      }
    >
      {error ? (
        <Alert variant="danger" className="mb-4">
          <AlertTitle>Could not reset</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Field
          label="New password"
          htmlFor="password"
          required
          error={form.formState.errors.password?.message}
        >
          <Input
            type="password"
            autoComplete="new-password"
            invalid={!!form.formState.errors.password}
            {...form.register('password')}
          />
        </Field>
        <Field
          label="Confirm password"
          htmlFor="confirm"
          required
          error={form.formState.errors.confirm?.message}
        >
          <Input
            type="password"
            autoComplete="new-password"
            invalid={!!form.formState.errors.confirm}
            {...form.register('confirm')}
          />
        </Field>
        <Button type="submit" size="lg" loading={form.formState.isSubmitting}>
          Update password
        </Button>
      </form>
    </AuthLayout>
  );
}
